import { z } from 'zod';
import {
  PALLET_STATUSES,
  type InvoiceLineConcept,
  type InvoiceStatus,
  type MovementType,
  type OutboundLineStatus,
  type PalletStatus,
} from '../enums';
import {
  idSchema,
  isoDate,
  isoDateTime,
  optionalText,
  paginationSchema,
  requiredText,
} from './common';

/** Máximo de palets en una misma operación de entrada. */
export const MAX_PALLETS_PER_RECEIPT = 500;

export const scanCodeSchema = z
  .string({ error: 'Campo obligatorio' })
  .trim()
  .min(1, 'Campo obligatorio')
  .max(60);

// ---------------------------------------------------------------- Entrada

export const palletEntryLineSchema = z.object({
  productId: idSchema,
  clientReference: optionalText(60),
  notes: optionalText(500),
  /** Opcional: ubicar el palet en la misma operación. */
  locationCode: optionalText(60),
});
export type PalletEntryLine = z.input<typeof palletEntryLineSchema>;

export const receivePalletsSchema = z.object({
  clientId: idSchema,
  /** Fecha y hora de entrada; por defecto, ahora. No puede ser futura. */
  receivedAt: isoDateTime.optional(),
  notes: optionalText(1000),
  pallets: z
    .array(palletEntryLineSchema)
    .min(1, 'Añada al menos un palet')
    .max(MAX_PALLETS_PER_RECEIPT, `Máximo ${MAX_PALLETS_PER_RECEIPT} palets por entrada`),
});
export type ReceivePalletsInput = z.input<typeof receivePalletsSchema>;
export type ReceivePalletsData = z.output<typeof receivePalletsSchema>;

export interface ReceivePalletsResult {
  receiptId: number;
  pallets: PalletDto[];
}

// ---------------------------------------------------------------- Ubicación y movimientos

export const locatePalletSchema = z.object({
  palletCode: scanCodeSchema,
  locationCode: scanCodeSchema,
  notes: optionalText(500),
});
export type LocatePalletInput = z.input<typeof locatePalletSchema>;

export interface LocatePalletResult {
  pallet: PalletDto;
  movementType: MovementType;
  fromLocationCode: string | null;
  toLocationCode: string;
}

// ---------------------------------------------------------------- Correcciones y anulación

export const updatePalletSchema = z.object({
  productId: idSchema,
  clientReference: optionalText(60),
  notes: optionalText(500),
});
export type UpdatePalletInput = z.input<typeof updatePalletSchema>;

export const cancelPalletSchema = z.object({ reason: requiredText(500) });

// ---------------------------------------------------------------- Consultas

export const PALLET_SCOPES = ['stock', 'history', 'all'] as const;
export type PalletScope = (typeof PALLET_SCOPES)[number];

export const PALLET_SORTS = [
  'enteredAt',
  '-enteredAt',
  'exitedAt',
  '-exitedAt',
  'location',
  'reference',
  'client',
] as const;
export type PalletSort = (typeof PALLET_SORTS)[number];

export const listPalletsQuerySchema = paginationSchema.extend({
  /** stock = en instalaciones; history = salidos/anulados; all = todos. */
  scope: z.enum(PALLET_SCOPES).default('stock'),
  status: z.enum(PALLET_STATUSES).optional(),
  clientId: idSchema.optional(),
  productId: idSchema.optional(),
  /** Referencia exacta del cliente (sin distinguir mayúsculas). */
  reference: z.string().trim().max(60).optional(),
  /** Prefijo de ubicación, tolerante a separadores ("A-01" → A-01-xx). */
  location: z.string().trim().max(60).optional(),
  /** Texto libre: código de palet, referencia, cliente o mercancía. */
  q: z.string().trim().max(100).optional(),
  enteredFrom: isoDate.optional(),
  enteredTo: isoDate.optional(),
  exitedFrom: isoDate.optional(),
  exitedTo: isoDate.optional(),
  sort: z.enum(PALLET_SORTS).default('-enteredAt'),
  /** Lista de ids separada por comas (p. ej. para imprimir etiquetas). */
  ids: z
    .string()
    .regex(/^\d+(,\d+){0,499}$/, 'Lista de ids no válida')
    .transform((value) => value.split(',').map(Number))
    .optional(),
});
export type ListPalletsParams = z.output<typeof listPalletsQuerySchema>;
export interface ListPalletsQuery {
  page?: number;
  pageSize?: number;
  scope?: PalletScope;
  status?: PalletStatus;
  clientId?: number;
  productId?: number;
  reference?: string;
  location?: string;
  q?: string;
  enteredFrom?: string;
  enteredTo?: string;
  exitedFrom?: string;
  exitedTo?: string;
  sort?: PalletSort;
  ids?: string;
}

export interface PalletDto {
  id: number;
  code: string;
  status: PalletStatus;
  clientId: number;
  clientName: string;
  productId: number;
  productName: string;
  clientReference: string | null;
  locationId: number | null;
  locationCode: string | null;
  inboundReceiptId: number;
  enteredAt: string;
  exitedAt: string | null;
  /** Días facturables según la regla configurada (a fecha de hoy si sigue dentro). */
  daysStored: number;
  notes: string | null;
}

export interface PalletMovementDto {
  id: number;
  type: MovementType;
  fromLocationCode: string | null;
  toLocationCode: string | null;
  movedAt: string;
  userName: string;
  notes: string | null;
}

export interface PalletOutboundDto {
  orderId: number;
  lineStatus: OutboundLineStatus;
  reservedAt: string;
  reservedBy: string;
  pickedAt: string | null;
  pickedBy: string | null;
  shippedAt: string | null;
  shippedBy: string | null;
}

export interface PalletBillingLineDto {
  invoiceId: number;
  invoiceNumber: string | null;
  invoiceStatus: InvoiceStatus;
  concept: InvoiceLineConcept;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  voided: boolean;
}

export const PALLET_EVENT_TYPES = [
  'RECEIVED',
  'PLACED',
  'MOVED',
  'RESERVED',
  'PICKED',
  'UNRESERVED',
  'RELEASED',
  'EXITED',
  'CANCELLED',
  'EDITED',
  'BILLED',
] as const;
export type PalletEventType = (typeof PALLET_EVENT_TYPES)[number];

export interface PalletEventDto {
  at: string;
  type: PalletEventType;
  description: string;
  userName: string | null;
}

export interface PalletDetailDto extends PalletDto {
  createdByName: string;
  receiptNotes: string | null;
  exitedByName: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  cancelReason: string | null;
  movements: PalletMovementDto[];
  outbound: PalletOutboundDto[];
  billing: PalletBillingLineDto[];
  timeline: PalletEventDto[];
}
